import React from "react";
import { Box, Skeleton, Stack, TableCell, TableRow, useTheme } from "@mui/material";

interface ProcessRowSkeletonProps {
  /** Adjusts the cell layout to the history header (hides live-only columns). */
  history?: boolean;
  /** Number of dynamic user-data columns currently rendered in the real header. */
  userDataKeysCount?: number;
}

/**
 * Single skeleton row, meant to be rendered *inside* ProcessesList's own
 * TableBody / virtualized Row — under the real (sticky) header — instead of
 * a separate overlaid table. This keeps the real header always on screen
 * (translated text, sticky positioning) with only the data rows swapped
 * for placeholders while loading, so there's no second header to overlap
 * or fight with the sticky one.
 *
 * Column layout mirrors TaskItem.tsx exactly, so cells stay aligned under
 * the real header both in live mode (process, userData[], activity, duration,
 * waiting, working, priority, status, participants, actions) and history mode
 * (process, userData[], duration, priority, collapse).
 */
export const ProcessRowSkeleton: React.FC<ProcessRowSkeletonProps> = ({
  history = false,
  userDataKeysCount = 0,
}) => {
  const theme = useTheme();
  const ROW_HEIGHT = 120; // matches the real TaskItem row height so cells align

  return (
    <TableRow
      sx={{
        height: ROW_HEIGHT,
        "& td": { borderBottom: `1px solid ${theme.palette.divider}` },
      }}
    >
      {/* Processo */}
      <TableCell>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Skeleton variant="circular" width={36} height={36} sx={{ flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="80%" height={16} />
            <Skeleton variant="text" width="55%" height={13} sx={{ mt: 0.5 }} />
          </Box>
        </Stack>
      </TableCell>

      {/* Colonne dinamiche user-data */}
      {Array.from({ length: userDataKeysCount }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton variant="text" width="70%" height={16} />
        </TableCell>
      ))}

      {/* Attività — solo live */}
      {!history && (
        <TableCell>
          <Skeleton variant="text" width="70%" height={16} />
          <Skeleton variant="text" width="40%" height={13} sx={{ mt: 0.5 }} />
        </TableCell>
      )}

      {/* Durata processo */}
      <TableCell>
        <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 3 }} />
      </TableCell>

      {/* Attesa + Lavoro — solo live */}
      {!history && (
        <>
          <TableCell>
            <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 3 }} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 3 }} />
          </TableCell>
        </>
      )}

      {/* Priorità */}
      <TableCell>
        <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: 4 }} />
      </TableCell>

      {/* Stato — solo live */}
      {!history && (
        <TableCell>
          <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 3 }} />
        </TableCell>
      )}

      {/* Partecipanti — solo live */}
      {!history && (
        <TableCell>
          <Stack direction="row" sx={{ "& > span": { ml: -0.75 } }}>
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="circular"
                width={30}
                height={30}
                sx={{
                  border: `2px solid ${theme.palette.background.paper}`,
                  flexShrink: 0,
                }}
              />
            ))}
          </Stack>
        </TableCell>
      )}

      {/* Azioni (live) o Collapse (history) */}
      <TableCell>
        <Skeleton variant="circular" width={30} height={30} />
      </TableCell>
    </TableRow>
  );
};

export default ProcessRowSkeleton;
